/*
  <sm-readmore> — collapses a block of copy to a few lines with a toggle.

  The whole text is in the page whether it is open or shut; only its height is
  clamped. Nothing is trimmed or loaded later, so the copy is there for search
  engines and for anyone reading with the stylesheet off.

  With no script the element does nothing and the CSS leaves the copy at full
  height, because the clamp is scoped to an attribute this file sets.
*/
if (!customElements.get('sm-readmore')) {
  customElements.define(
    'sm-readmore',
    class SmReadMore extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('[data-readmore-body]');
        this.toggle = this.querySelector('[data-readmore-toggle]');
        if (!this.body || !this.toggle) return;

        /* Only clamp once the script is running. Until then the copy sits at
           its natural height, so a failed script never hides anything. */
        this.setAttribute('data-ready', '');

        this.open = false;
        this.body.id = this.body.id || `readmore-${Math.random().toString(36).slice(2, 8)}`;
        this.toggle.setAttribute('aria-controls', this.body.id);

        this.toggle.addEventListener('click', () => this.set(!this.open));

        /* If the copy turns out to fit anyway, at this width or after a
           resize, the control is pointless: drop the clamp and hide it. */
        this.measure = this.measure.bind(this);
        this.resize = new ResizeObserver(this.measure);
        this.resize.observe(this.body);
        window.addEventListener('resize', this.measure, { passive: true });
        this.measure();
      }

      disconnectedCallback() {
        if (this.resize) this.resize.disconnect();
        window.removeEventListener('resize', this.measure);
      }

      /* Compare against the clamp the stylesheet asks for, not the height the
         body happens to have. Measuring the rendered height meant that during
         the closing animation, while the body was still tall, the copy looked
         like it fitted; the control then removed its own clamp and the block
         stayed open. Skip entirely while animating, when max-height is none
         and there is nothing to compare against. */
      measure() {
        if (this.open || this.animating) return;
        const clamp = parseFloat(getComputedStyle(this.body).maxHeight);
        if (!Number.isFinite(clamp)) return;
        this.classList.toggle('is-unnecessary', this.body.scrollHeight <= clamp + 2);
      }

      set(open) {
        this.open = open;
        this.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', String(open));
        this.toggle.textContent = open
          ? this.toggle.dataset.less || 'Read less'
          : this.toggle.dataset.more || 'Read more';

        /* max-height cannot animate to none, so open to the measured height
           and hand control back once the transition has run. */
        this.animating = true;
        const settle = () => {
          this.animating = false;
          this.body.removeEventListener('transitionend', settle);
          if (this.open) this.body.style.maxHeight = 'none';
        };
        this.body.addEventListener('transitionend', settle);
        // A transition that never starts would leave the flag stuck on.
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(settle, 600);

        if (open) {
          this.body.style.maxHeight = `${this.body.scrollHeight}px`;
        } else {
          this.body.style.maxHeight = `${this.body.scrollHeight}px`;
          requestAnimationFrame(() => {
            this.body.style.maxHeight = '';
          });
        }
      }
    }
  );
}
